import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowRight } from "lucide-react";
import { authConfig } from "../config/auth";
import { useAuth } from "../contexts/AuthContext";
import ContentRightsModal from "../components/ContentRightsModal";
import "../styles/accept-terms.css";

export default function AcceptTermsPage() {
  const navigate = useNavigate();
  const { token, user, setUser } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const companyName = user?.companyName ?? user?.companyId ?? "Your Company";

  const handleContinue = async () => {
    if (!accepted) return;

    setLoading(true);
    setError("");

    try {
      if (!token) {
        setError("Session expired. Please log in again.");
        navigate("/login");
        return;
      }

      const response = await fetch(`${authConfig.apiUrl}/api/auth/accept-terms`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.message || "Failed to accept terms. Please try again.");
        setLoading(false);
        return;
      }

      const data = await response.json();

      // Update local user data
      if (data.user) {
        setUser(data.user);
      }

      // Navigate to dashboard
      navigate("/dashboard");
    } catch (err) {
      setError("Network error. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="accept-terms-container">
      <div className="accept-terms-card">
        <div className="accept-terms-icon">
          <Shield size={32} />
        </div>

        <h1 className="accept-terms-title">Welcome to CrewCast!</h1>

        <p className="accept-terms-subtitle">
          Before you continue, please review and accept our content usage terms.
        </p>

        <div className="accept-terms-checkbox-container">
          <input
            type="checkbox"
            id="accept-terms"
            className="accept-terms-checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            disabled={loading}
          />
          <label htmlFor="accept-terms" className="accept-terms-label">
            I agree to the{" "}
            <button
              type="button"
              className="accept-terms-link"
              onClick={(e) => {
                e.preventDefault();
                setShowModal(true);
              }}
            >
              Content Release Terms
            </button>{" "}
            and certify that {companyName} has the rights to any content I submit.
          </label>
        </div>

        <button
          type="button"
          className="accept-terms-button"
          onClick={handleContinue}
          disabled={!accepted || loading}
        >
          {loading ? "Please wait..." : "Continue"}
          {!loading && <ArrowRight size={18} />}
        </button>

        {error && <div className="accept-terms-error">{error}</div>}
      </div>

      {showModal && (
        <ContentRightsModal
          companyName={companyName}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
